import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Target, Eye, MousePointerClick, Wallet, Loader2, CheckCircle2,
} from "lucide-react";

/**
 * MA-CAMPAIGN-INCLUSION-LAYER-035: Post-sync campaign review modal
 *
 * Shown after a successful sync when new/unreviewed campaigns are discovered.
 * User selects which campaigns to track in MissionAssist measurement.
 */
export default function CampaignInclusionModal({ open, onOpenChange, onComplete }) {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get("/campaign-inclusion/campaigns?filter=unreviewed");
        setCampaigns(res.data.campaigns || []);
        setSelected(new Set());
      } catch {
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(campaigns.map((c) => c.campaign_id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const handleIncludeSelected = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      // Include selected
      await api.put("/campaign-inclusion/update", {
        campaign_ids: Array.from(selected),
        included_in_measurement: true,
      });
      // Mark the rest as reviewed but excluded
      const skipped = campaigns
        .filter((c) => !selected.has(c.campaign_id))
        .map((c) => c.campaign_id);
      if (skipped.length > 0) {
        await api.put("/campaign-inclusion/update", {
          campaign_ids: skipped,
          included_in_measurement: false,
        });
      }
      toast({
        title: "Campaigns updated",
        description: `${selected.size} campaign(s) now tracked in MissionAssist.`,
      });
      onOpenChange(false);
      onComplete?.();
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleSkipForNow = async () => {
    // Mark all as reviewed but not included
    if (campaigns.length > 0) {
      try {
        await api.put("/campaign-inclusion/update", {
          campaign_ids: campaigns.map((c) => c.campaign_id),
          included_in_measurement: false,
        });
      } catch {
        // Silent — skip is non-critical
      }
    }
    onOpenChange(false);
    onComplete?.();
  };

  const fmt = (n) => (n || 0).toLocaleString();
  const fmtMoney = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-slate-900 border-white/10 text-slate-100" data-testid="campaign-inclusion-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-indigo-400" />
            New campaigns found from Meta
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-1">
            Choose which campaigns to track in MissionAssist measurement.
            Only included campaigns will appear in War Room and count toward ROAS, CAC, CPL, and other performance metrics.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="py-8 text-center text-slate-400">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
            <p>All campaigns have been reviewed.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between text-xs text-slate-400 px-1">
              <span>{selected.size} of {campaigns.length} selected</span>
              <div className="flex gap-2">
                <button onClick={selectAll} className="hover:text-slate-200 underline" data-testid="inclusion-select-all">
                  Select All
                </button>
                <button onClick={deselectAll} className="hover:text-slate-200 underline" data-testid="inclusion-deselect-all">
                  Deselect All
                </button>
              </div>
            </div>

            {/* Campaign List */}
            <div className="max-h-[360px] overflow-y-auto space-y-1 pr-1">
              {campaigns.map((c) => (
                <div
                  key={c.campaign_id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    selected.has(c.campaign_id)
                      ? "bg-indigo-500/10 border-indigo-500/30"
                      : "bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.04]"
                  }`}
                  onClick={() => toggleSelect(c.campaign_id)}
                  data-testid={`inclusion-campaign-${c.campaign_id}`}
                >
                  <Checkbox
                    checked={selected.has(c.campaign_id)}
                    onCheckedChange={() => toggleSelect(c.campaign_id)}
                    className="border-slate-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-100 truncate">{c.name}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {c.source_platform && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-600 text-slate-400">
                          {c.source_platform}
                        </Badge>
                      )}
                      {c.meta_status && (
                        <span className={c.meta_status === "ACTIVE" ? "text-emerald-400" : "text-slate-500"}>
                          {c.meta_status}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-400 flex-shrink-0">
                    <span className="flex items-center gap-1" title="Spend">
                      <Wallet className="h-3 w-3" /> {fmtMoney(c.spend)}
                    </span>
                    <span className="flex items-center gap-1" title="Impressions">
                      <Eye className="h-3 w-3" /> {fmt(c.impressions)}
                    </span>
                    <span className="flex items-center gap-1" title="Clicks">
                      <MousePointerClick className="h-3 w-3" /> {fmt(c.clicks)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleSkipForNow}
            disabled={saving}
            className="text-slate-400 hover:text-slate-200"
            data-testid="inclusion-skip-btn"
          >
            Skip for Now
          </Button>
          <Button
            onClick={handleIncludeSelected}
            disabled={saving || selected.size === 0}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="inclusion-include-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Target className="h-4 w-4 mr-2" />
            )}
            Track {selected.size > 0 ? `${selected.size} Campaign${selected.size > 1 ? "s" : ""}` : "Selected"} in MissionAssist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
